import React from 'react';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import {
  calculateStrategyLegPnl,
  isActiveStrategyLeg,
  normalizeSymbol,
  resolveStrategyLegMarkPrice,
} from '../../../components/trading-dashboard/portfolioValue';
import compactStyles from '../../../components/trading/CompactTable.module.css';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { usePositionState } from '../shared/usePositionState';
import { useStrategyActions } from '../shared/useStrategyActions';
import { useStrategyLegEntryBasis } from '../shared/useStrategyLegEntryBasis';
import { WidgetErrorBoundary } from '../shared/WidgetErrorBoundary';
import { StrategyCloseModal, StrategyViewModal } from '../shared/StrategyModals';
import { fmtPrice, fmtTime, toFiniteNumber, pnlClass } from '../shared/formatters';
import { useState } from 'react';
import { StrategyPositionSnapshot } from '../../../types/trading';

const StrategyPositionsInner: React.FC = () => {
  const { strategyPositions, loading, error } = usePositionState();
  const { ticksBySymbol } = useOrderTicketNavigation();

  const [localStrategyPositions, setLocalStrategyPositions] = useState<StrategyPositionSnapshot[] | null>(null);
  const effectivePositions = localStrategyPositions ?? strategyPositions;

  // Sync from hook when upstream changes (unless local override is active after a close)
  React.useEffect(() => {
    setLocalStrategyPositions(null);
  }, [strategyPositions]);

  const strategyActions = useStrategyActions(
    // Wrap to update local state
    ((updater) => {
      setLocalStrategyPositions((prev) => {
        const current = prev ?? strategyPositions;
        return typeof updater === 'function' ? updater(current) : updater;
      });
    }) as React.Dispatch<React.SetStateAction<StrategyPositionSnapshot[]>>,
  );

  const basisRef = useStrategyLegEntryBasis(effectivePositions);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', color: 'var(--tr-text-primary, #e2e8f0)' }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px',
        background: 'var(--tr-bg-secondary, rgba(255,255,255,0.02))',
        borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
        fontSize: '0.8125rem', fontWeight: 700, gap: '16px',
      }}>
        <span>
          Strategy Positions
          {!loading && (
            <span style={{ fontSize: '0.75rem', color: 'var(--tr-text-muted)', marginLeft: '6px' }}>
              ({effectivePositions.length})
            </span>
          )}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 }}>
          Loading…
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', color: 'var(--tr-pnl-negative, #ef4444)', fontSize: '0.875rem', flex: 1 }}>
          {error}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <table className={compactStyles.compactTable}>
            <thead>
              <tr>
                <th>Leg Id</th>
                <th>Symbol</th>
                <th className={compactStyles.numericCell}>Net Qty</th>
                <th className={compactStyles.numericCell}>Avg Cost Basis</th>
                <th className={compactStyles.numericCell}>Last Price</th>
                <th className={compactStyles.numericCell}>PnL</th>
                <th>Trade At</th>
                <th>Updated At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {effectivePositions.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--tr-text-muted)', padding: '16px' }}>
                    No strategy positions
                  </td>
                </tr>
              )}
              {effectivePositions.map((sp) => {
                const isExp = !!strategyActions.expandedStrategies[sp.strategy_position_id];
                const visLegs = sp.legs.filter(isActiveStrategyLeg);
                return (
                  <React.Fragment key={sp.strategy_position_id}>
                    {/* Strategy parent row */}
                    <tr
                      style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}
                      onClick={() => strategyActions.toggleStrategyExpanded(sp.strategy_position_id)}
                    >
                      <td
                        colSpan={9}
                        style={{
                          padding: '4px 8px', fontSize: '0.8rem',
                          borderBottom: '1px solid rgba(255,255,255,0.08)',
                          color: 'var(--tr-text-secondary, #ccc)', fontWeight: 600,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}>
                              {isExp ? '▼' : '▶'}
                            </span>
                            <span style={{ marginRight: '12px' }}>{sp.name}</span>
                            <span style={{ marginRight: '12px', fontSize: '0.74rem', color: 'var(--tr-text-muted, #888)' }}>
                              Legs: {visLegs.length}
                            </span>
                          </div>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                            <button
                              type="button"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '3px 8px', border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '4px', color: 'var(--tr-text-secondary, #ccc)',
                                background: 'rgba(255,255,255,0.04)', fontSize: '0.74rem',
                                fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                              onClick={(e) => { e.stopPropagation(); strategyActions.openStrategyViewModal(sp); }}
                              title="View strategy configuration"
                              aria-label={`View strategy configuration for ${sp.name}`}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '3px 8px', border: '1px solid rgba(239,68,68,0.4)',
                                borderRadius: '4px', color: '#fca5a5',
                                background: 'rgba(255,255,255,0.04)', fontSize: '0.74rem',
                                fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                              onClick={(e) => { e.stopPropagation(); strategyActions.openStrategyCloseModal(sp); }}
                              title="Close strategy row"
                              aria-label={`Close strategy ${sp.name}`}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded leg rows */}
                    {isExp && visLegs.map((leg) => {
                      const sym = normalizeSymbol(leg.symbol) || leg.symbol || '--';
                      const normSym = normalizeSymbol(leg.symbol);
                      const liveTick = normSym ? ticksBySymbol[normSym] : undefined;
                      const lp = resolveStrategyLegMarkPrice(leg, ticksBySymbol);
                      const legKey = `${sp.strategy_position_id}:${leg.leg_id}`;
                      const nqv = toFiniteNumber(leg.net_qty);
                      const nq = nqv != null ? nqv.toLocaleString() : '--';

                      let acb = toFiniteNumber(leg.avg_price);
                      if (acb == null && (nqv ?? 0) !== 0) {
                        const rem = basisRef.current[legKey];
                        if (typeof rem === 'number' && Number.isFinite(rem)) {
                          acb = rem;
                        } else if (typeof lp === 'number' && Number.isFinite(lp)) {
                          acb = lp;
                          basisRef.current[legKey] = lp;
                        }
                      }

                      const rawPnl = calculateStrategyLegPnl(
                        { ...leg, avg_price: acb ?? leg.avg_price, net_qty: nqv ?? leg.net_qty },
                        ticksBySymbol,
                      );
                      const pnl = rawPnl ?? toFiniteNumber(leg.pnl);
                      const tradeAt = leg.trade_at ?? sp.created_at;
                      const updatedAt = liveTick?.ts ?? leg.updated_at ?? sp.updated_at;

                      return (
                        <tr key={`${sp.strategy_position_id}-${leg.leg_id}`} style={{ background: 'rgba(255,255,255,0.01)' }}>
                          <td>{leg.leg_id || '--'}</td>
                          <td>{sym}</td>
                          <td className={compactStyles.numericCell}>{nq}</td>
                          <td className={compactStyles.numericCell}>{fmtPrice(acb)}</td>
                          <td className={compactStyles.numericCell}>{fmtPrice(lp)}</td>
                          <td className={`${compactStyles.numericCell} ${pnlClass(pnl)}`}>{fmtPrice(pnl)}</td>
                          <td>{tradeAt ? fmtTime(tradeAt) : '--'}</td>
                          <td>{updatedAt ? fmtTime(updatedAt) : '--'}</td>
                          <td>{leg.action || '--'}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Strategy Close Confirmation Modal */}
      {strategyActions.strategyCloseTarget && (
        <StrategyCloseModal
          target={strategyActions.strategyCloseTarget}
          onDismiss={strategyActions.dismissStrategyCloseModal}
          onConfirm={strategyActions.handleConfirmStrategyClose}
          submitting={strategyActions.strategyCloseSubmitting}
          error={strategyActions.strategyCloseError}
        />
      )}

      {/* Strategy View Configuration Modal */}
      {strategyActions.strategyViewTarget && (
        <StrategyViewModal
          target={strategyActions.strategyViewTarget}
          config={strategyActions.strategyViewConfig}
          onClose={strategyActions.closeStrategyViewModal}
        />
      )}
    </div>
  );
};

export const StrategyPositionsWidget: React.FC<WidgetInstanceProps> = (_props) => (
  <WidgetErrorBoundary name="Strategy Positions">
    <StrategyPositionsInner />
  </WidgetErrorBoundary>
);

export default StrategyPositionsWidget;
